import React, { useEffect, useRef, useState } from "react";
import { Shield, XCircle } from "lucide-react";
import { Hud, Petunjuk, Pop, POP_CSS, useCountdown } from "../Hud";
import { FONT } from "../../kota/ui";
import { JalanRaya, LAJUR } from "./JalanRaya";

const DURASI = 60, TARGET = 15, CEK_Y = 0.72;
const LABEL = { helm: "Tanpa helm", boncengan: "Boncengan tiga", plat: "Plat bodong" };
const WARNA_KENDARAAN = ["#0f8f8f", "#c0583a", "#2e58c4", "#b7791f", "#f4f1e6", "#6b8e5a"];

// Mini game Polisi — Razia di Alun-alun: kendaraan melaju dari atas melewati garis razia. Ketuk yang melanggar sebelum lewat
// (motor tanpa helm, boncengan tiga, plat bodong/merah) → +1 "Tilang!"; menilang yang tertib → −1 "Salah tilang!"; pelanggar lolos
// tidak dihitung. 60 dtk, skor = poin/15×100. Level ↑ → kendaraan lebih cepat & rapat.
function Kendaraan({ k, onTap }) {
  const motor = k.jenis === "motor";
  const w = motor ? 34 : 54, h = motor ? 60 : 92;
  const helm = k.pelanggaran !== "helm";
  const plat = k.pelanggaran === "plat";
  return (
    <button data-testid={`kendaraan-${k.id}`} data-pelanggaran={k.pelanggaran || ""} data-status={k.ditilang ? "ditilang" : k.lolos ? "lolos" : "jalan"} aria-label={k.pelanggaran ? LABEL[k.pelanggaran] : "Tertib"}
      onPointerDown={e => { e.preventDefault(); onTap(k.id); }}
      style={{ position: "absolute", left: k.ditilang ? (k.lane ? "86%" : "14%") : `${LAJUR[k.lane]}%`, top: k.y, transform: "translate(-50%,-50%)", width: w + 16, height: h + 12, padding: "6px 8px", border: 0, background: "transparent", cursor: "pointer", opacity: k.ditilang ? .55 : 1, transition: "opacity .35s, left .45s ease-out", WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display: "block", filter: "drop-shadow(0 4px 6px rgba(0,0,0,.25))" }}>
        {motor ? (
          <>
            <rect x="12" y="4" width="10" height="52" rx="4" fill={k.warna} />
            <rect x="9" y="2" width="16" height="6" rx="3" fill="#141414" />
            {k.pelanggaran === "boncengan" && <><circle cx="17" cy="44" r="6.5" fill="#f1c9a5" /><path d="M10.5 44 a6.5 6.5 0 0 1 13 0 Z" fill="#4a2c1a" /></>}
            {k.pelanggaran === "boncengan" && <><circle cx="17" cy="33" r="6.5" fill="#f1c9a5" /><path d="M10.5 33 a6.5 6.5 0 0 1 13 0 Z" fill="#8a5a3c" /></>}
            <circle cx="17" cy={k.pelanggaran === "boncengan" ? 22 : 28} r="7.5" fill={helm ? k.helmWarna : "#f1c9a5"} />
            {helm ? <rect x="10.5" y={(k.pelanggaran === "boncengan" ? 22 : 28) - 2} width="13" height="4" rx="2" fill="#141414" opacity=".7" /> : <path d={`M9.5 ${28} a7.5 7.5 0 0 1 15 0 Z`} fill="#141414" />}
          </>
        ) : (
          <>
            <rect x="2" y="2" width={w - 4} height={h - 4} rx="12" fill={k.warna} />
            <rect x="9" y="14" width={w - 18} height="16" rx="4" fill="#8fb6c9" /><rect x="9" y={h - 34} width={w - 18} height="12" rx="4" fill="#8fb6c9" />
            <rect x="6" y="4" width="8" height="5" rx="2" fill="#fff3b0" /><rect x={w - 14} y="4" width="8" height="5" rx="2" fill="#fff3b0" />
          </>
        )}
        {/* plat nomor belakang: hitam = sah, merah polos = bodong */}
        <rect x={w / 2 - 11} y={h - 9} width="22" height="7" rx="1.5" fill={plat ? "#dc2626" : "#141414"} stroke="#fff" strokeWidth="1" />
        {!plat && <text x={w / 2} y={h - 3.6} textAnchor="middle" fontSize="4.5" fontWeight="800" fill="#fff" fontFamily="monospace">{k.plat}</text>}
      </svg>
    </button>
  );
}

export function Razia({ shift, onSelesai }) {
  const lv = shift.level || 1;
  const KECEPATAN = 110 + (lv - 1) * 8, JEDA = Math.max(650, 1150 - (lv - 1) * 45);
  const H = window.innerHeight, CEK = H * CEK_Y;
  const r = useRef(null);
  if (!r.current) {
    let n = 0;
    const rnd = () => { const x = Math.sin(shift.seed + (n++) * 97.3) * 10000; return x - Math.floor(x); };
    r.current = { rnd, items: [], id: 0, next: 0, poin: 0, tilang: 0, salah: 0, lolos: 0, selesai: false, last: 0, k: 0 };
  }
  const [items, setItems] = useState([]);
  const [stat, setStat] = useState({ poin: 0, tilang: 0, salah: 0 });
  const [pops, setPops] = useState([]);
  const pop = (x, y, teks, warna) => setPops(ps => [...ps.slice(-4), { x, y, teks, warna, k: r.current.k++ }]);

  function akhiri() { const s = r.current; if (s.selesai) return; s.selesai = true; onSelesai(Math.max(0, Math.min(100, Math.round((s.poin / TARGET) * 100)))); }
  const sisa = useCountdown(DURASI, akhiri);

  useEffect(() => {
    const s = r.current; let raf;
    const loop = now => {
      if (s.selesai) return;
      const dt = Math.min(0.05, (now - (s.last || now)) / 1000); s.last = now;
      if (now > s.next) {
        const u = s.rnd(), motor = s.rnd() < 0.6;
        const pel = u < 0.45 ? (motor ? (s.rnd() < 0.4 ? "helm" : s.rnd() < 0.5 ? "boncengan" : "plat") : "plat") : null;
        const lane = s.rnd() < 0.5 ? 0 : 1;
        // jangan menumpuk di lajur yang sama
        if (!s.items.some(k => k.lane === lane && k.y < 110)) {
          s.items.push({ id: s.id++, jenis: motor ? "motor" : "mobil", lane, y: -60, pelanggaran: pel, warna: WARNA_KENDARAAN[Math.floor(s.rnd() * WARNA_KENDARAAN.length)], helmWarna: ["#dc2626", "#2e58c4", "#f2c230", "#141414"][Math.floor(s.rnd() * 4)], plat: `AG ${1000 + Math.floor(s.rnd() * 9000)}`, ditilang: false, lolos: false });
          s.next = now + JEDA + s.rnd() * 300;
        }
      }
      for (const k of s.items) {
        k.y += KECEPATAN * dt * (k.ditilang ? 0.3 : 1);   // yang ditilang menepi ke bahu jalan, melambat
        if (!k.lolos && !k.ditilang && k.y > CEK + 20) { k.lolos = true; if (k.pelanggaran) { s.lolos += 1; pop(LAJUR[k.lane], CEK, "Lolos…", "#e4e1d4"); } }
      }
      s.items = s.items.filter(k => k.y < H + 80);
      setItems(s.items.slice());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line

  function tap(id) {
    const s = r.current, k = s.items.find(x => x.id === id);
    if (!k || s.selesai || k.ditilang || k.lolos) return;
    k.ditilang = true;
    if (k.pelanggaran) { s.poin += 1; s.tilang += 1; pop(LAJUR[k.lane], k.y - 50, `Tilang! ${LABEL[k.pelanggaran]}`, "#ffe08a"); }
    else { s.poin = Math.max(0, s.poin - 1); s.salah += 1; pop(LAJUR[k.lane], k.y - 50, "Salah tilang! −1", "#ffb4b4"); }
    setStat({ poin: s.poin, tilang: s.tilang, salah: s.salah });
  }

  return (
    <>
      <style>{POP_CSS}</style>
      <JalanRaya items={[]} riderY={null}>
        {/* garis & spanduk razia + kerucut + polisi di trotoar kanan */}
        <div data-testid="garis-razia" style={{ position: "absolute", left: "27%", right: "27%", top: CEK, height: 0, borderTop: "3px dashed #f2c230" }} />
        <div style={{ position: "absolute", left: "27%", right: "27%", top: CEK - 30, display: "flex", justifyContent: "space-between", padding: "0 6px" }}>
          {[0, 1, 2, 3].map(i => <span key={i} style={{ width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderBottom: "16px solid #e07a1f" }} />)}
        </div>
        <div style={{ position: "absolute", left: "27%", right: "27%", top: CEK + 14, textAlign: "center", fontFamily: FONT }}><span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 6, background: "#141414", color: "#fff", fontSize: 10.5, fontWeight: 800, letterSpacing: 2 }}>RAZIA</span></div>
        <div data-testid="polisi-kecil" style={{ position: "absolute", left: "77%", top: CEK - 36 }}>
          <svg viewBox="0 0 40 72" width="40" height="72"><ellipse cx="20" cy="68" rx="12" ry="4" fill="rgba(0,0,0,.2)" /><rect x="10" y="26" width="20" height="30" rx="7" fill="#2e4a8a" /><rect x="2" y="30" width="8" height="18" rx="4" fill="#2e4a8a" /><rect x="30" y="28" width="8" height="18" rx="4" fill="#2e4a8a" /><rect x="4" y="22" width="4" height="12" fill="#f1c9a5" /><rect x="2" y="12" width="8" height="12" rx="3" fill="#fff" /><circle cx="20" cy="18" r="9" fill="#f1c9a5" /><path d="M9 16 a11 11 0 0 1 22 0 Z" fill="#2e4a8a" /><rect x="8" y="14" width="24" height="3" fill="#141414" /><rect x="12" y="56" width="6" height="12" fill="#141414" /><rect x="22" y="56" width="6" height="12" fill="#141414" /></svg>
        </div>
        {items.map(k => <Kendaraan key={k.id} k={k} onTap={tap} />)}
        {pops.map(p => <Pop key={p.k} x={`${p.x}%`} y={p.y} warna={p.warna}>{p.teks}</Pop>)}
      </JalanRaya>
      <Hud sisa={sisa} durasi={DURASI} poin={stat.poin} extra={[[Shield, "#0000CD", `${stat.tilang}/${TARGET}`], [XCircle, "#dc2626", `${stat.salah}`]]} />
      <Petunjuk>Tilang: tanpa helm · boncengan tiga · plat merah</Petunjuk>
    </>
  );
}
